// Порядок строк — отображение, не данные: массив id в личной настройке (проекты — поверх общего
// дефолта из shared_setting, люди — поверх алфавита). Незнакомое — в конец по дате создания:
// новый проект не может пропасть у того, кто его ещё не расставлял. Спека 2026-09-14-display-settings.
import { useCallback, useMemo } from 'react';
import { data, useSetting, useTable } from '@core/store';
import type { Row } from '@core/store/model';
import { assignablePeople } from '@core/people';

export const PROJECT_ORDER = 'projects.order';
export const PEOPLE_ORDER = 'people.order';

type Orderable = { id: string; name: string; created_at: string };

/** Строки в сохранённом порядке; чего в нём нет — в конец по дате создания, потом по имени. */
export function orderRows<T extends Orderable>(rows: Iterable<T>, saved: readonly string[] | null): T[] {
  const rank = new Map(saved?.map((id, i) => [id, i]) ?? []);
  return [...rows].sort((a, b) => {
    const ra = rank.get(a.id), rb = rank.get(b.id);
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return a.created_at.localeCompare(b.created_at) || a.name.localeCompare(b.name);
  });
}

/** По алфавиту — для списков выбора и для людей без своего порядка. */
export const alphabetical = <T extends { name: string }>(rows: Iterable<T>): T[] => [...rows].sort((a, b) => a.name.localeCompare(b.name));

/**
 * Новый массив после броска: fromId встаёт на место, где сейчас anchorId (null — в конец). Индекс берётся
 * по списку с fromId внутри — так считает индикатор в useRowReorder, и при сдвиге вниз строка ложится
 * ровно под ту, над которой отпустили.
 */
export function reorderIds(current: readonly string[], fromId: string, anchorId: string | null): string[] {
  const target = anchorId === null ? current.length : current.indexOf(anchorId);
  const rest = current.filter((id) => id !== fromId);
  if (target < 0) return [...rest, fromId];
  const at = Math.min(target, rest.length);
  return [...rest.slice(0, at), fromId, ...rest.slice(at)];
}

/**
 * Куда вставить строку-призрак при перестановке: индекс строки сетки перед которой она встаёт, или null,
 * если цели нет или бросок ничего не изменит (своё место и место сразу за собой). toIndex — место среди ids,
 * firstRowOf — первая строка сущности, afterLast — строка сразу за последней переставляемой.
 */
export function ghostRowIndex(drag: { id: string; toIndex: number | null } | null, ids: readonly string[], firstRowOf: (id: string) => number, afterLast: number): number | null {
  if (!drag || drag.toIndex === null) return null;
  const from = ids.indexOf(drag.id);
  if (drag.toIndex === from || drag.toIndex === from + 1) return null;
  const id = ids[drag.toIndex];
  return id === undefined ? afterLast : firstRowOf(id);
}

/** Личный порядок отличается от общего дефолта — есть что сбрасывать. */
export const differsFromDefault = (personal: readonly string[] | null, shared: readonly string[] | null): boolean =>
  personal !== null && JSON.stringify(personal) !== JSON.stringify(shared ?? []);

/** Общий порядок проектов по умолчанию — из shared_setting; пустой, если админ его ещё не задал. */
export function useDefaultProjectOrder(): string[] {
  const shared = useTable('shared_setting');
  return useMemo(() => {
    const row = [...shared.values()].find((r) => r.key === PROJECT_ORDER);
    return Array.isArray(row?.value) ? (row.value as string[]) : [];
  }, [shared]);
}

/**
 * Порядок проектов на экране: личный, иначе общий дефолт, иначе по дате создания. `reorder` пишет
 * личный — первое перетаскивание копирует то, что человек видел, и сдвигает в нём одну строку.
 * `reset` возвращает к общему.
 */
export function useProjectOrder(): { rows: Row<'project'>[]; ids: string[]; differs: boolean; reorder: (fromId: string, anchorId: string | null) => Promise<void>; reset: () => Promise<void> } {
  const projects = useTable('project');
  const [personal, setPersonal] = useSetting<string[] | null>(PROJECT_ORDER, null);
  const sharedOrder = useDefaultProjectOrder();
  const rows = useMemo(() => orderRows(projects.values(), personal ?? sharedOrder), [projects, personal, sharedOrder]);
  const ids = useMemo(() => rows.map((p) => p.id), [rows]);
  const reorder = useCallback(async (fromId: string, anchorId: string | null) => {
    const seen = orderRows(data.store.getState().tables.project.values(), data.setting<string[] | null>(PROJECT_ORDER, null) ?? sharedOrder).map((p) => p.id);
    await data.setSetting(PROJECT_ORDER, reorderIds(seen, fromId, anchorId));
  }, [sharedOrder]);
  const reset = useCallback(async () => { setPersonal(null); }, [setPersonal]);
  return { rows, ids, differs: differsFromDefault(personal, sharedOrder), reorder, reset };
}

/** Порядок людей: личный, иначе алфавит. Только активные и назначаемые — остальным на People не место. */
export function usePeopleOrder(): { rows: Row<'person'>[]; reorder: (fromId: string, anchorId: string | null) => Promise<void> } {
  const people = useTable('person');
  const [personal] = useSetting<string[] | null>(PEOPLE_ORDER, null);
  const rows = useMemo(() => {
    const active = assignablePeople(people.values());
    return personal ? orderRows(active, personal) : active;
  }, [people, personal]);
  const reorder = useCallback(async (fromId: string, anchorId: string | null) => {
    const active = assignablePeople(data.store.getState().tables.person.values());
    const saved = data.setting<string[] | null>(PEOPLE_ORDER, null);
    const seen = (saved ? orderRows(active, saved) : active).map((p) => p.id);
    await data.setSetting(PEOPLE_ORDER, reorderIds(seen, fromId, anchorId));
  }, []);
  return { rows, reorder };
}
